import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { InputBase, InputLabel } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { Field, SelectedAddressProps } from "../../../Interface/scheduler/form";
import {
  AppConstants,
  noAddressMsg,
  sharedStateKeys,
} from "../../../constants/constant";
import { useFormContext } from "../../contexts/FormContext";
import AddressDropdown from "./AddressDropdown";
import { useSharedStateWithActions } from "../../hooks/useSharedState";
import { fetchAddressSuggestions } from "../../../service/apiService";
import { isPostalCodeMatch } from "../../../utility/utility";
import { validateZipCode } from "../Helper/Helper";
import { useErrorClassClassifier } from "../../hooks/useErrorClassClassifier";

interface InputFieldProps {
  field: Field;
  error?: string;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleEmailBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
}

const InputField: React.FC<InputFieldProps> = ({
  field,
  error,
  handleChange,
  handleEmailBlur,
}) => {
  const inputId =
    field.id === AppConstants.EMAIL_ID || field.id === AppConstants.ZIPCODE_ID
      ? `agnos-${field.id}`
      : field.id;
  const { formValues, setFormValues } = useFormContext();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [apiResults, setApiResults] = useState([]);
  const { setValue } = useSharedStateWithActions();

  const handleAddressSelect = (selectedAddress: SelectedAddressProps) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      houseNumber: selectedAddress.houseNumber || "",
      street: selectedAddress.street || "",
      building: selectedAddress.building || "",
    }));

    const {
      title = "",
      houseNumber = "",
      street = "",
      building = "",
      label = "",
      city = AppConstants.NO_CITY_FOUND,
      state = "",
    } = selectedAddress;
    
    const constructedAddress = [houseNumber, street, building]
      .filter((part) => part !== "")
      .join(" ");
    
    const isTitleALocationComponent = 
      title === formValues.zipcode ||
      title.toLowerCase() === city.toLowerCase() ||
      title.toLowerCase() === state.toLowerCase() ||
      /^\d{5}(-\d{4})?$/.test(title);
    
    const isTitleInConstructedAddress = 
      constructedAddress && 
      constructedAddress.toLowerCase().includes(title.toLowerCase());
    
    const shouldPrependTitle = 
      title && 
      !isTitleALocationComponent &&
      constructedAddress &&
      !isTitleInConstructedAddress &&
      label.toLowerCase().startsWith(title.toLowerCase() + ",");
    
    const address1 = shouldPrependTitle
      ? `${title}, ${constructedAddress}`
      : constructedAddress || "No address collected";
    
      
    setFormValues((prevValues) => {
      const newValues = { ...prevValues };
      if (field.id === "address") {
        newValues.address = label;
        newValues.address1 = address1;
        if (!formValues.city) {
          newValues.city = city;
        }
        if (!formValues.state) {
          newValues.state = state;
        }
      } else if (field.id === "mobile-address") {
        newValues["mobile-address"] = label;
      }
      return newValues;
    });
    setIsDropdownVisible(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const sAddressParam = sessionStorage.getItem("sAddressParam");
    if (sAddressParam && sAddressParam !== "undefined") {
      setFormValues((prevValues) => ({
        ...prevValues,
        zipcode: sAddressParam,
      }));
    }
  }, []);

  const handleChangeWrapper = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "zipcode" && /(0{5,})/.test(value as string)) {
      return;
    }
    handleChange(e);
    setIsLoading(true);
    try {
      if (
        (name === "address" || name === "mobile-address") &&
        (value as string)?.length >= 3
      ) {
        let formValueZipCode;
        if (name === "address") {
          formValueZipCode = formValues.zipcode;
        } else {
          formValueZipCode = formValues["mobile-zipcode"];
        }
        const suggestions = await fetchAddressSuggestions(
          name,
          value,
          formValueZipCode,
          setFormValues,
          setIsLoading,
        );
        sessionStorage.setItem("selectedAddressFromDropdown", "false");
        if (validateZipCode(formValueZipCode as string)) {
          const zipCodeFromFormValues = formValueZipCode
            .replaceAll(/\s/g, "")
            .toLowerCase();
          const res = suggestions?.filter((item) =>
            isPostalCodeMatch(item, zipCodeFromFormValues, noAddressMsg),
          );
          setApiResults(res);
          setValue(sharedStateKeys.S_STATE_ADDRESS_SUGGESTION_RES, res);
          setIsDropdownVisible(true);
        } else {
          setApiResults(suggestions);
          setIsDropdownVisible(true);
        }
      }
    } catch (error) {
      console.error("Error fetching address suggestions", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onHandleFieldBlur = (e) => {
    handleEmailBlur(e);
  };

  const [realTimeEmailValidationError, setRealTimeValidationError] = useState({
    isRealTime: false,
    message: "",
    styles: { display: "flex", alignItems: "center", gap: "10px" },
  });
  useEffect(() => {
    const isEmailValidationError =
      field.type === "email" && error?.includes("SendGridResponseFalse");
    const errorMessage = field.realTimeEmailValidationMessage
      ? field.realTimeEmailValidationMessage
      : "Entered email is not valid";
    setRealTimeValidationError((prev) => ({
      ...prev,
      isRealTime: isEmailValidationError,
      message: errorMessage,
    }));
  }, [error]);

  return (
    <>
      {field.id === "zipcode" && field["width"] === "50%" && (
        <div style={{ width: "100%" }} />
        //This is to add a spacer div when zipcode field has 50% width. Without this the layout will collapse zipcode into previous element if it fits.
      )}
      <div
        className="input-label-wrap"
        key={field.id}
        style={{
          flex: (() => {
            if (["100%", "55%", "60%", "35%"].includes(field.width || "")) {
              return "inherit";
            }
            if (field.id === "zipcode" || field.id === "commercialInputFlow") {
              return 1;
            }
            return "";
          })(),
          width: field.width ?? "",
          flexGrow: field.id === "address2" ? 1 : undefined,
        }}
      >
        <InputLabel className="label-basic">
          {field.label}
          {field.required === true && <sup>*</sup>}
        </InputLabel>
        <InputBase
          placeholder={field.placeholder}
          name={field.id}
          id={inputId}
          className={useErrorClassClassifier({
            error: !!error?.length,
            errorClassToApply: "input-basic-highlight",
            defaultClass: `input-basic valid-field ${error ? "error" : ""}`,
          })}
          value={formValues[field.id] || ""}
          onChange={handleChangeWrapper}
          key={field.id}
          aria-label={field.placeholder}
          onBlur={onHandleFieldBlur}
          type={field.type}
        />
        {(field.id === "address" || field.id === "mobile-address") &&
          isDropdownVisible &&
          apiResults?.length > 0 && (
            <div ref={dropdownRef}>
              <AddressDropdown
                apiResults={apiResults}
                onSelect={handleAddressSelect}
                isLoading={isLoading}
              />
            </div>
          )}
        {error && (
          <div
            className={`${realTimeEmailValidationError.isRealTime ? "realtime-email-validation-error-message" : "error-message"}`}
          >
            {realTimeEmailValidationError.isRealTime ? (
              <div style={realTimeEmailValidationError.styles}>
                <ErrorOutlineOutlinedIcon
                  sx={{
                    color: "#C81E1E",
                    fontSize: "small",
                    maxWidth: "18px",
                    maxHeight: "18px",
                  }}
                />
                <span>{realTimeEmailValidationError.message}</span>
              </div>
            ) : (
              <>{error}</>
            )}
          </div>
        )}
      </div>
      {(field.id === "zipcode" || field.id === "commercialInputFlow") &&
        Number.parseInt(field.width) !== 100 && (
          <div
            style={{
              width: `calc(${100 - Number.parseInt(field.width)}% - 24.25px)`,
            }}
          ></div>
        )}
    </>
  );
};

export default InputField;
